import { motion, AnimatePresence } from 'framer-motion';
import { Radio, Video } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { aurora, auroraPalette } from '@/components/aurora/tokens';

interface MobileHuddleStickyBannerProps {
  /** Quando false → banner non renderizzato (animazione di uscita gestita). */
  active: boolean;
  /** Etichetta principale (es. "Huddle in corso · Team Vendite"). */
  label: string;
  /** Sottotitolo opzionale (es. "3 partecipanti"). */
  subtitle?: string;
  /** È attiva una registrazione → mostra badge REC pulsante. */
  recording?: boolean;
  onJoin: () => void;
  onDismiss?: () => void;
  className?: string;
}

/**
 * Banner sticky huddle. Presentational: lo stato (esistenza huddle attiva
 * nel workgroup corrente) è iniettato dal chiamante via `active`. Le pagine
 * Gruppi/Canali (#1721/#1722) leggono lo store huddle quando sarà esposto e
 * passano i prop qui.
 */
export default function MobileHuddleStickyBanner({
  active,
  label,
  subtitle,
  recording,
  onJoin,
  onDismiss,
  className,
}: MobileHuddleStickyBannerProps) {
  return (
    <AnimatePresence initial={false}>
      {active && (
        <motion.div
          initial={{ y: -16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -16, opacity: 0 }}
          transition={aurora.spring.snappy}
          className={cn(
            'sticky z-30 px-3 py-2 border-b border-border/60',
            'text-white shadow-md',
            className,
          )}
          style={{
            top: 'env(safe-area-inset-top)',
            background: `linear-gradient(135deg, ${auroraPalette.indigo}, ${auroraPalette.violet})`,
          }}
          role="status"
          aria-live="polite"
          data-testid="mobile-huddle-banner"
        >
          <div className="flex items-center gap-3">
            <span
              className="h-9 w-9 rounded-full bg-white/15 inline-flex items-center justify-center shrink-0"
              aria-hidden
            >
              <Video className="w-4 h-4" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={cn(aurora.type.bodyStrong, 'truncate')}>{label}</span>
                {recording && (
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/15 text-[10px] font-bold uppercase tracking-wide"
                    aria-label="Registrazione in corso"
                    data-testid="mobile-huddle-rec"
                  >
                    <Radio className="w-3 h-3 animate-pulse" />
                    REC
                  </span>
                )}
              </div>
              {subtitle && (
                <p className={cn(aurora.type.caption, 'truncate text-white/80')}>{subtitle}</p>
              )}
            </div>
            <Button
              type="button"
              onClick={onJoin}
              size="sm"
              className="h-9 px-3 bg-white text-foreground hover:bg-white/90 rounded-full shrink-0"
              data-testid="mobile-huddle-join"
            >
              Entra
            </Button>
            {onDismiss && (
              <button
                type="button"
                onClick={onDismiss}
                aria-label="Nascondi banner"
                className="h-9 w-9 inline-flex items-center justify-center rounded-full text-white/70 hover:bg-white/10"
                data-testid="mobile-huddle-dismiss"
              >
                ×
              </button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
